import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { NumericTextField } from '../../../../components/forms/NumericTextField';
import type { RockUCSConformityReading } from '../../../../types/rockUcs';
import type { ConformityPreview } from '../computations';
import { ASTM_TOLERANCE_REFERENCE, CORE_SIZE_OPTIONS, mmToMicron, micronToMm } from '../rockUcsFields';
import { ConformityCharts } from './ConformityCharts';
import { inputSx, paperSx, SectionHeader, unit } from '../sectionStyles';

/**
 * Structural view of the general-row fields this section edits — shared by
 * Rock UCS (RockUCSGeneral) and Rock UCS No Strain (RockUCSNSGeneral), so the
 * conformity UI is one component, not a per-test clone (BUG-2026-0138 item 5).
 */
export interface ConformityGeneralFields {
  coreSize: string | null;
  sideStraightnessMm: number | null;
  conformityFailReason: string | null;
}

interface Props {
  general: ConformityGeneralFields;
  conformity: RockUCSConformityReading[];
  preview: ConformityPreview;
  /** Live L/D from the specimen preview (shown in the results dashboard). */
  lOverD: number | null;
  shapeStatement: string | null;
  onChange: (field: keyof ConformityGeneralFields, value: number | string | null) => void;
  onConformityChange: (rows: RockUCSConformityReading[]) => void;
  readOnly?: boolean;
}

/**
 * Live "Conformity Results" row — label, numeric metric, pass/fail chip.
 * Mirrors the workbook's Conformity Results table (e.g. Perpendicularity
 * Deviation 0.0958 Pass) so values can be compared 1:1 against KeyLAB.
 */
const ResultRow = ({ label, value, pass }: { label: string; value: string; pass: boolean | null }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
    <Typography variant="body2" sx={{ fontSize: '0.76rem', flex: 1 }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontSize: '0.76rem', fontWeight: 600, minWidth: 56, textAlign: 'right' }}>
      {value}
    </Typography>
    <Chip
      size="small"
      label={pass === null ? '—' : pass ? 'Pass' : 'Fail'}
      color={pass === null ? 'default' : pass ? 'success' : 'error'}
      sx={{ height: 20, fontSize: '0.68rem', minWidth: 52 }}
    />
  </Box>
);

const num4 = (v: number | null): string => (v === null || !Number.isFinite(v) ? '—' : v.toFixed(4));

/**
 * REQ-01 — Conformity & ASTM shape tolerance: 11-point flatness entry per end
 * per diameter line, live pass/fail dashboard, ASTM reference popup, and the
 * auto-generated report statement preview. Charts are internal-only.
 */
export const ConformitySection = ({
  general, conformity, preview, lOverD, shapeStatement, onChange, onConformityChange, readOnly,
}: Props) => {
  const [refOpen, setRefOpen] = useState(false);
  const lOverDDisplay = lOverD === null || !Number.isFinite(lOverD) ? null : lOverD.toFixed(2);

  const seriesValues = useMemo(() => {
    const byKey: Record<string, { positionMm: number; valueMm: number | null }[]> = {};
    for (const end of [1, 2]) {
      for (const line of [1, 2]) {
        byKey[`${end}-${line}`] = conformity
          .filter((r) => r.endNumber === end && r.diameterLine === line)
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((r) => ({ positionMm: r.positionMm, valueMm: r.valueMm }));
      }
    }
    return byKey;
  }, [conformity]);

  const positions = seriesValues['1-1']?.map((p) => p.positionMm) ?? [];

  const setValue = (end: number, line: number, sortOrder: number, value: number | null) => {
    onConformityChange(conformity.map((r) =>
      r.endNumber === end && r.diameterLine === line && r.sortOrder === sortOrder
        ? { ...r, valueMm: value }
        : r,
    ));
  };

  return (
    <Paper variant="outlined" sx={paperSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <SectionHeader label="2. Conformity & ASTM Shape Tolerance" />
        <Button
          size="small"
          startIcon={<InfoOutlined sx={{ fontSize: 14 }} />}
          onClick={() => setRefOpen(true)}
          sx={{ textTransform: 'none', fontSize: '0.7rem' }}
        >
          ASTM tolerance reference
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)', gap: 1.5 }}>
        {/* ── Entry side ── */}
        <Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
            <TextField
              select size="small" label="Core Size"
              value={general.coreSize ?? 'HQ'}
              onChange={(e) => onChange('coreSize', e.target.value)}
              sx={{ ...inputSx, width: 110 }}
              InputProps={{ readOnly }}
            >
              {CORE_SIZE_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
            <NumericTextField
              size="small" label="Side Straightness"
              value={general.sideStraightnessMm}
              onChange={(v) => onChange('sideStraightnessMm', v)}
              InputProps={{ endAdornment: unit('mm'), readOnly }}
              sx={{ ...inputSx, width: 150 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
              Dial readings in µm at {positions.length} points ({general.coreSize ?? 'HQ'}: 0–{positions[positions.length - 1] ?? '…'} mm)
            </Typography>
          </Box>

          {/* Entry grid: position rows × 4 series columns */}
          <Table size="small" sx={{ '& td, & th': { p: 0.25, border: 0 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem' }}>Pos (mm)</Typography>
                </TableCell>
                {[
                  'End 1 · Dia 1', 'End 1 · Dia 2', 'End 2 · Dia 1', 'End 2 · Dia 2',
                ].map((h) => (
                  <TableCell key={h} align="center">
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem' }}>{h}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {positions.map((pos, idx) => (
                <TableRow key={pos}>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{pos}</Typography>
                  </TableCell>
                  {[[1, 1], [1, 2], [2, 1], [2, 2]].map(([end, line]) => (
                    <TableCell key={`${end}-${line}`} align="center">
                      <NumericTextField
                        size="small"
                        value={mmToMicron(seriesValues[`${end}-${line}`]?.[idx]?.valueMm ?? null)}
                        onChange={(v) => setValue(end, line, idx, micronToMm(v))}
                        InputProps={{ readOnly }}
                        sx={{ ...inputSx, width: '100%', maxWidth: 110 }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Conformity Results dashboard (numeric, matching the workbook table) */}
          <Box sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2.5, rowGap: 0.5 }}>
            <ResultRow label="Side Tolerance (≤0.5 mm)" value={num4(general.sideStraightnessMm)} pass={preview.sideStraightnessPass} />
            <ResultRow label="Perpendicularity Dev. (≤0.25°)" value={num4(preview.perpendicularityDeg)} pass={preview.perpendicularityPass} />
            <ResultRow label="Flatness Max Dev. (≤0.025 mm)" value={num4(preview.flatnessMaxDevMm)} pass={preview.flatnessPass} />
            <ResultRow label="Parallelism Dev. (≤0.13°)" value={num4(preview.parallelismDeg)} pass={preview.parallelismPass} />
            <ResultRow
              label="L/D Ratio (2.0–2.5)"
              value={preview.lOverDPass === null ? '—' : (lOverDDisplay ?? '—')}
              pass={preview.lOverDPass}
            />
          </Box>

          <TextField
            size="small" fullWidth multiline minRows={1}
            label="Fail reason (optional, appended to the report statement)"
            value={general.conformityFailReason ?? ''}
            onChange={(e) => onChange('conformityFailReason', e.target.value || null)}
            InputProps={{ readOnly }}
            sx={{ ...inputSx, mt: 1.25 }}
          />
          {shapeStatement && (
            <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.75, fontSize: '0.7rem' }}>
              Report comment: {shapeStatement}
            </Typography>
          )}
        </Box>

        {/* ── Inline charts (internal only) ── */}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block', mb: 0.5 }}>
            Conformity charts — internal verification only (not included in the report)
          </Typography>
          <ConformityCharts preview={preview} values={seriesValues} />
        </Box>
      </Box>

      {/* ASTM tolerance reference popup */}
      <Dialog open={refOpen} onClose={() => setRefOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem' }}>ASTM Shape Tolerance Reference (D4543 / D7012)</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Check</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Requirement</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ASTM_TOLERANCE_REFERENCE.map((r) => (
                <TableRow key={r.check}>
                  <TableCell>{r.check}</TableCell>
                  <TableCell>{r.requirement}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Values are fixed ASTM constants. Only the ultrasonic consistency-checker tolerance is adjustable.
          </Typography>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};
